import {
  useId,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import { History, Lock, Power, RotateCcw, UserPlus } from "lucide-react";
import { color, layout, radius, space } from "@tokens/index";
import { Badge } from "@primitives/Badge";
import { Button } from "@primitives/Button";
import { Stack } from "@primitives/Stack";
import { Text } from "@primitives/Text";
import { useMeasuredInsertion } from "@primitives/useMeasuredInsertion";
import { useViewport } from "@primitives/viewport";
import type { QueueMember, QueueSummary, UserSearchResult } from "../../../experience-shell/queues";
import { useNewRowDetector } from "../workflow-board/useNewRowDetector";
import { MemberRow } from "./MemberRow";
import { NameEditor } from "./NameEditor";
import { UserPicker } from "./UserPicker";

// ---------------------------------------------------------------------------
// Public types
//
// QueueDetail is a pure presentation composition: it takes server state +
// loading flags + a debounced search-results slice, and emits intent
// callbacks. The owning page wires React Query mutations + routing.
// Keeping this boundary pure makes the composition portable to Storybook
// and independent of the BFF transport.
// ---------------------------------------------------------------------------

export interface QueueDetailProps {
  readonly queue: QueueSummary;
  readonly canEdit: boolean;
  readonly canManageLifecycle: boolean;
  // User ids that have muted push notifications for this queue. A membership
  // test (not a per-member flag on QueueMember) because the data is sourced
  // from a separate read; absent ids render as "notifications on".
  readonly mutedUserIds: ReadonlySet<string>;
  readonly searchTerm: string;
  readonly searchResults: readonly UserSearchResult[];
  readonly isSearching: boolean;
  readonly searchError: Error | null;
  readonly isRenaming: boolean;
  readonly renameError: Error | null;
  readonly isAddingMember: boolean;
  readonly addMemberError: Error | null;
  readonly removingUserId: string | null;
  readonly removeMemberError: Error | null;
  readonly isChangingStatus: boolean;
  readonly lifecycleError: Error | null;
  readonly onRename: (name: string) => void;
  readonly onSearchTermChange: (term: string) => void;
  readonly onAddMember: (user: UserSearchResult) => void;
  readonly onRemoveMember: (member: QueueMember) => void;
  readonly onDeactivate: () => void;
  readonly onReactivate: () => void;
  /** Opens the audit timeline. Renders the Timeline action in the header when set. */
  readonly onOpenTimeline?: () => void;
}

// ---------------------------------------------------------------------------
// Style sheet
// ---------------------------------------------------------------------------

const styles = {
  root: {
    display: "flex",
    flexDirection: "column" as const,
    gap: space[5],
    width: "100%",
    minWidth: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: space[3],
    minWidth: 0,
  },
  chipRow: {
    display: "flex",
    gap: space[2],
    alignItems: "center",
    flexWrap: "wrap" as const,
  },
  // Sections are separated by tone and a hairline under a caps label, not
  // by cards. The label row is 28px so a trailing glyph has room.
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space[3],
    minHeight: 28,
    paddingBottom: space[2],
    borderBottom: `1px solid ${color.border.subtle}`,
    minWidth: 0,
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column" as const,
    gap: space[3],
    paddingTop: space[3],
    minWidth: 0,
  },
  readOnlyNote: {
    display: "flex",
    alignItems: "center",
    gap: space[3],
    paddingInline: space[2],
    minWidth: 0,
  },
  memberList: {
    display: "flex",
    flexDirection: "column" as const,
    width: "100%",
  },
  error: {
    paddingInline: space[4],
    paddingBlock: space[3],
    borderRadius: radius.md,
    background: color.status.dangerBg,
  },
} as const;

// ---------------------------------------------------------------------------
// Local pieces
// ---------------------------------------------------------------------------

function ErrorLine({ error }: { readonly error: Error | null }): ReactElement | null {
  if (error === null) return null;
  return (
    <div style={styles.error} role="alert">
      <Text role="bodySm" tone="danger">
        {error.message}
      </Text>
    </div>
  );
}

interface SectionProps {
  readonly title: string;
  readonly trailing?: ReactNode;
  readonly children: ReactNode;
}

function Section({ title, trailing, children }: SectionProps): ReactElement {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} style={{ width: "100%", minWidth: 0 }}>
      <div style={styles.sectionHeader}>
        <Text as="h2" id={titleId} role="caps" size="xs" tone="muted" truncate>
          {title}
        </Text>
        {trailing}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </section>
  );
}

function ReadOnlyNote({ children }: { readonly children: string }): ReactElement {
  return (
    <div style={styles.readOnlyNote}>
      <Lock size={16} strokeWidth={2} aria-hidden="true" color={color.fg.muted} />
      <Text role="bodySm" tone="muted">
        {children}
      </Text>
    </div>
  );
}

/**
 * One member list item. Only a row that joins the list after mount grows in
 * (entrance policy); a settled row carries no `initial`. The row clips only
 * while it grows or leaves, so a focus ring is never cut at rest.
 */
function MemberListItem({
  isNew,
  children,
}: {
  readonly isNew: boolean;
  readonly children: ReactNode;
}): ReactElement {
  const { motionProps, contentRef, growing } = useMeasuredInsertion(isNew);
  const isPresent = useIsPresent();
  return (
    <motion.div
      role="listitem"
      {...motionProps}
      style={{ overflow: growing || !isPresent ? "hidden" : "visible" }}
    >
      <div ref={contentRef}>{children}</div>
    </motion.div>
  );
}

function Chip({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <Badge variant="neutral" size="sm" caps={false}>
      {children}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QueueDetail({
  queue,
  canEdit,
  canManageLifecycle,
  mutedUserIds,
  searchTerm,
  searchResults,
  isSearching,
  searchError,
  isRenaming,
  renameError,
  isAddingMember,
  addMemberError,
  removingUserId,
  removeMemberError,
  isChangingStatus,
  lifecycleError,
  onRename,
  onSearchTermChange,
  onAddMember,
  onRemoveMember,
  onDeactivate,
  onReactivate,
  onOpenTimeline,
}: QueueDetailProps): ReactElement {
  const { isCompact } = useViewport();
  const isLastMember = queue.members.length <= 1;
  const memberIds = useMemo(() => new Set(queue.members.map((m) => m.userId)), [queue.members]);
  const memberKeys = useMemo(() => queue.members.map((m) => m.userId), [queue.members]);
  const isNewRow = useNewRowDetector(memberKeys);

  // Members present at mount are "settled"; anyone who joins later is fresh
  // and settles from a tint. The set is fixed at mount on purpose: a fresh
  // row must not lose its tint when the server copy replaces the
  // provisional one.
  const settledIdsRef = useRef<ReadonlySet<string> | null>(null);
  if (settledIdsRef.current === null) settledIdsRef.current = memberIds;
  const settledIds = settledIdsRef.current;

  const statusPanelStyle: CSSProperties = {
    display: "flex",
    flexDirection: isCompact ? "column" : "row",
    alignItems: isCompact ? "stretch" : "center",
    justifyContent: "space-between",
    gap: space[3],
    paddingInline: space[2],
    minWidth: 0,
  };

  return (
    <div style={styles.root}>
      <div style={styles.headerRow}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <NameEditor
            currentName={queue.name}
            canEdit={canEdit}
            isSaving={isRenaming}
            error={renameError}
            onSave={onRename}
          />
        </div>
        <div style={styles.chipRow}>
          {queue.isPersonal && (
            <Chip>
              <Lock size={12} strokeWidth={2} aria-hidden="true" />
              <Text role="caption" style={{ color: "inherit", lineHeight: 1 }}>
                Personal queue
              </Text>
            </Chip>
          )}
          <Chip>
            {queue.memberCount} {queue.memberCount === 1 ? "member" : "members"}
          </Chip>
          {queue.status !== "active" && (
            <Badge variant="warning" size="sm">
              {queue.status === "deactivating" ? "DEACTIVATING" : "DEACTIVATED"}
            </Badge>
          )}
        </div>
        {onOpenTimeline !== undefined && (
          <div style={{ marginLeft: "auto" }}>
            <Button variant="ghost" size="sm" onClick={onOpenTimeline}>
              <History size={14} strokeWidth={2.25} aria-hidden="true" />
              Timeline
            </Button>
          </div>
        )}
      </div>

      {(canManageLifecycle || queue.status !== "active") && (
        <Section title="Status">
          <div style={statusPanelStyle}>
            <Stack direction="column" gap={1}>
              <Text role="body" weight="semibold">
                {queue.status === "active"
                  ? "Queue is active"
                  : queue.status === "deactivating"
                    ? "Queue is deactivating"
                    : "Queue is deactivated"}
              </Text>
              <Text role="caption" tone="muted" style={{ maxWidth: layout.proseMaxWidth }}>
                {queue.status === "active"
                  ? "Deactivation makes this queue read-only and removes it from operational target pickers."
                  : queue.status === "deactivating"
                    ? "Deactivation is in progress. Editing is disabled until the transition completes."
                    : "This queue is read-only and cannot receive new operational work."}
              </Text>
              {lifecycleError !== null && (
                <Text role="caption" tone="danger">
                  {lifecycleError.message}
                </Text>
              )}
            </Stack>
            {canManageLifecycle && queue.status !== "deactivated" && (
              <Button variant="ghost" size="md" disabled={isChangingStatus} onClick={onDeactivate}>
                <Power size={14} strokeWidth={2.25} aria-hidden="true" />
                {isChangingStatus
                  ? "Deactivating…"
                  : queue.status === "deactivating"
                    ? "Retry deactivation"
                    : "Deactivate queue"}
              </Button>
            )}
            {canManageLifecycle && queue.status === "deactivated" && (
              <Button
                variant="primary"
                size="md"
                disabled={isChangingStatus}
                onClick={onReactivate}
              >
                <RotateCcw size={14} strokeWidth={2.25} aria-hidden="true" />
                {isChangingStatus ? "Reactivating…" : "Reactivate queue"}
              </Button>
            )}
          </div>
        </Section>
      )}

      <Section title="Members">
        <ErrorLine error={removeMemberError} />
        <div style={styles.memberList} role="list" aria-label="Queue members">
          <AnimatePresence initial={false}>
            {queue.members.map((member) => (
              <MemberListItem key={member.userId} isNew={isNewRow(member.userId)}>
                <MemberRow
                  member={member}
                  notificationsMuted={mutedUserIds.has(member.userId)}
                  canRemove={canEdit && !isLastMember}
                  isRemoving={removingUserId === member.userId}
                  isFresh={!settledIds.has(member.userId)}
                  onRemove={() => onRemoveMember(member)}
                />
              </MemberListItem>
            ))}
          </AnimatePresence>
        </div>
      </Section>

      {canEdit && (
        <Section
          title="Add a member"
          trailing={
            <span aria-hidden="true" style={{ display: "inline-flex", color: color.fg.muted }}>
              <UserPlus size={16} strokeWidth={2} />
            </span>
          }
        >
          <ErrorLine error={addMemberError} />
          <UserPicker
            searchTerm={searchTerm}
            results={searchResults}
            isSearching={isSearching}
            error={searchError}
            isAdding={isAddingMember}
            excludedUserIds={memberIds}
            onSearchTermChange={onSearchTermChange}
            onPick={onAddMember}
          />
        </Section>
      )}

      {!canEdit && queue.isPersonal && (
        <ReadOnlyNote>Personal queues are read-only. Membership is fixed to you.</ReadOnlyNote>
      )}
      {!canEdit && !queue.isPersonal && queue.status !== "active" && (
        <ReadOnlyNote>
          Deactivated queues are read-only. Reactivate this queue to edit it.
        </ReadOnlyNote>
      )}
    </div>
  );
}
